import { useMemo, useState } from 'react';
import { formatMoney, t } from '@aquabox/shared';
import { ApiError } from '../api/client';
import { useOrderMutations, useProducts, useVehicles } from '../api/queries';
import { CustomerPicker } from '../components/CustomerPicker';
import { useToast } from '../components/Toast';
import type { Customer, PaymentType } from '../api/types';

interface Line {
  productId: string;
  qty: number;
}

const PAYMENTS: PaymentType[] = ['CASH', 'CARD', 'ONLINE'];

/**
 * Sipariş gir modalı (§3.7). Tutar SUNUCUDA hesaplanır; buradaki toplam yalnız önizlemedir
 * (kural 4: para hesabı frontend'de yapılmaz).
 */
export function NewOrderModal({ onClose }: { onClose: () => void }) {
  const products = useProducts();
  const vehicles = useVehicles();
  const { create } = useOrderMutations();
  const toast = useToast();

  // Müşteri ZORUNLU: veresiye/zimmet yalnız kayıtlı müşteriye yazılır (sunucu da 400 döner).
  const [customer, setCustomer] = useState<Customer | null>(null);
  // "Farklı adrese teslim": kapalıyken sipariş adresi müşterinin kayıtlı adresidir (sunucu
  // kopyalar); açıkken yalnız bu siparişe özel adres gider, müşteri kaydı değişmez.
  const [otherAddress, setOtherAddress] = useState(false);
  const [address, setAddress] = useState('');
  const [addressNote, setAddressNote] = useState('');

  /** Müşteri değişince önceki müşteriye girilmiş özel adres yeni müşteriye taşınmaz. */
  function changeCustomer(next: Customer | null): void {
    setCustomer(next);
    setOtherAddress(false);
    setAddress('');
  }
  const [payment, setPayment] = useState<PaymentType>('CASH');
  const [vehicleId, setVehicleId] = useState<string>('');
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string | null>(null);

  const productList = products.data?.items ?? [];
  const vehicleList = vehicles.data?.items ?? [];

  // İlk ürün gelince tek satırlık sepeti hazırla.
  const effectiveLines = useMemo<Line[]>(
    () =>
      lines.length > 0 ? lines : productList[0] ? [{ productId: productList[0].id, qty: 1 }] : [],
    [lines, productList],
  );

  /** Önizleme toplamı: sunucunun döndüğü tutar esastır, bu yalnız kullanıcıya tahmin gösterir. */
  const previewTotal = effectiveLines.reduce((sum, line) => {
    const product = productList.find((p) => p.id === line.productId);
    return sum + (product ? Number(product.unitPrice) * line.qty : 0);
  }, 0);

  function setLine(index: number, patch: Partial<Line>): void {
    setLines(effectiveLines.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  async function submit(assign: boolean): Promise<void> {
    setError(null);
    if (!customer) return;
    const trimmedAddress = address.trim();
    if (otherAddress && trimmedAddress.length < 3) {
      setError(t('newOrder.otherAddress.required'));
      return;
    }
    try {
      const order = await create.mutateAsync({
        source: 'PHONE',
        customerId: customer.id,
        address: otherAddress ? trimmedAddress : undefined,
        addressNote: addressNote.trim() || undefined,
        items: effectiveLines,
        paymentType: payment,
        vehicleId: assign && vehicleId ? vehicleId : undefined,
        autoAssign: !assign || !vehicleId,
      });
      toast(
        order.vehicle
          ? t('panel.orderCreatedAssigned', { plate: order.vehicle.plate })
          : t('panel.orderCreated'),
      );
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('error.internal'));
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('panel.newOrderTitle')}
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card modal-card" style={{ '--modal-w': '640px' } as React.CSSProperties}>
        <h2 style={{ fontWeight: 600, fontSize: 22, lineHeight: 1.2 }}>
          {t('panel.newOrderTitle')}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 6, marginBottom: 20 }}>
          {t('panel.newOrderHint')}
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 16 }}>
          {t('newOrder.requiredHint')}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <span className="label">
              {t('newOrder.customer.label')}{' '}
              <span aria-hidden="true" style={{ color: 'var(--danger)' }}>
                *
              </span>
            </span>
            {customer ? (
              <div
                data-testid="order-customer-selected"
                style={{ display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{customer.fullName}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {[customer.phone, customer.address].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn"
                  style={{ color: 'var(--primary)', fontSize: 13 }}
                  onClick={() => changeCustomer(null)}
                >
                  {t('newOrder.customer.change')}
                </button>
              </div>
            ) : (
              <CustomerPicker onSelect={changeCustomer} />
            )}
          </div>

          {customer && (
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  role="switch"
                  data-testid="order-other-address-toggle"
                  checked={otherAddress}
                  onChange={(e) => setOtherAddress(e.target.checked)}
                />
                {t('newOrder.otherAddress.toggle')}
              </label>
              {otherAddress && (
                <div style={{ marginTop: 10 }}>
                  <label className="label" htmlFor="no-address">
                    {t('newOrder.otherAddress.label')}
                  </label>
                  <input
                    id="no-address"
                    className="input"
                    data-testid="order-other-address"
                    value={address}
                    maxLength={300}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                  <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '6px 0 0' }}>
                    {t('newOrder.otherAddress.hint')}
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="label" htmlFor="no-note">
              {t('panel.addressNote')}
            </label>
            <input
              id="no-note"
              className="input"
              value={addressNote}
              onChange={(e) => setAddressNote(e.target.value)}
            />
          </div>

          <div>
            <span className="label">{t('panel.product')}</span>
            {effectiveLines.map((line, index) => (
              <div
                key={index}
                style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}
              >
                <select
                  className="input"
                  style={{ flex: 1 }}
                  value={line.productId}
                  onChange={(e) => setLine(index, { productId: e.target.value })}
                  aria-label={t('panel.product')}
                >
                  {productList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {formatMoney(p.unitPrice)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary"
                  aria-label="Adet azalt"
                  onClick={() => setLine(index, { qty: Math.max(1, line.qty - 1) })}
                  style={{
                    width: 44,
                    height: 48,
                    justifyContent: 'center',
                    borderRadius: 'var(--r-input)',
                  }}
                >
                  −
                </button>
                <input
                  className="input"
                  style={{ width: 70, textAlign: 'center' }}
                  inputMode="numeric"
                  aria-label={t('panel.quantity')}
                  value={line.qty}
                  onChange={(e) => {
                    const n = Number(e.target.value.replace(/\D/g, ''));
                    setLine(index, { qty: Math.min(400, Math.max(1, n || 1)) });
                  }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  aria-label="Adet arttır"
                  onClick={() => setLine(index, { qty: Math.min(400, line.qty + 1) })}
                  style={{
                    width: 44,
                    height: 48,
                    justifyContent: 'center',
                    borderRadius: 'var(--r-input)',
                  }}
                >
                  +
                </button>
                {effectiveLines.length > 1 && (
                  <button
                    type="button"
                    className="btn"
                    style={{ color: 'var(--danger)', fontSize: 13 }}
                    onClick={() => setLines(effectiveLines.filter((_, i) => i !== index))}
                  >
                    {t('panel.removeItem')}
                  </button>
                )}
              </div>
            ))}
            {productList[0] && (
              <button
                type="button"
                className="btn"
                style={{ color: 'var(--primary)', fontSize: 13, padding: 0 }}
                onClick={() =>
                  setLines([...effectiveLines, { productId: productList[0]!.id, qty: 1 }])
                }
              >
                {t('panel.addItem')}
              </button>
            )}
          </div>

          <div>
            <span className="label">{t('panel.payment')}</span>
            <div style={{ display: 'flex', gap: 10 }}>
              {PAYMENTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPayment(p)}
                  aria-pressed={payment === p}
                  style={{
                    flex: 1,
                    height: 48,
                    borderRadius: 'var(--r-input)',
                    border: payment === p ? '2px solid var(--primary)' : '1px solid var(--border)',
                    background: payment === p ? 'var(--primary-bg)' : 'var(--surface)',
                    color: payment === p ? 'var(--primary)' : 'var(--text-secondary)',
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  {t(`paymentType.${p}`)}
                </button>
              ))}
            </div>
          </div>

          {payment === 'ONLINE' && (
            <p
              data-testid="online-payment-note"
              style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '-8px 0 0' }}
            >
              {t('newOrder.payment.onlineNote')}
            </p>
          )}

          <div>
            <span className="label">{t('panel.assignToVehicle')}</span>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setVehicleId('')}
                aria-pressed={vehicleId === ''}
                style={{
                  minHeight: 44,
                  padding: '0 16px',
                  borderRadius: 'var(--r-input)',
                  border: vehicleId === '' ? '2px solid var(--primary)' : '1px solid var(--border)',
                  background: vehicleId === '' ? 'var(--primary-bg)' : 'var(--surface)',
                  color: vehicleId === '' ? 'var(--primary)' : 'var(--text-secondary)',
                  fontWeight: 600,
                  fontSize: 13.5,
                }}
              >
                {t('panel.autoByRegion')}
              </button>
              {vehicleList.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVehicleId(v.id)}
                  aria-pressed={vehicleId === v.id}
                  style={{
                    minHeight: 44,
                    padding: '0 16px',
                    borderRadius: 'var(--r-input)',
                    border:
                      vehicleId === v.id ? '2px solid var(--primary)' : '1px solid var(--border)',
                    background: vehicleId === v.id ? 'var(--primary-bg)' : 'var(--surface)',
                    color: vehicleId === v.id ? 'var(--primary)' : 'var(--text-secondary)',
                    fontWeight: 600,
                    fontSize: 13.5,
                  }}
                >
                  {v.plate}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div role="alert" style={{ color: 'var(--danger)', fontSize: 13.5, fontWeight: 500 }}>
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 22,
            paddingTop: 18,
            borderTop: '1px solid var(--border)',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 15 }}>
            {t('panel.total')}: {formatMoney(previewTotal)}
          </span>
          <span style={{ flex: 1 }} />
          {!customer && (
            <span
              data-testid="order-customer-missing"
              style={{ fontSize: 12.5, color: 'var(--text-secondary)', maxWidth: 220 }}
            >
              {t('newOrder.customer.required')}
            </span>
          )}
          <button
            type="button"
            className="btn"
            style={{ color: 'var(--text-secondary)' }}
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
            disabled={create.isPending || !customer}
            onClick={() => void submit(false)}
          >
            {t('panel.save')}
          </button>
          <button
            type="button"
            className={`btn btn-primary ${create.isPending ? 'btn-loading' : ''}`}
            disabled={create.isPending || !customer}
            onClick={() => void submit(true)}
          >
            {create.isPending && <span className="spinner" />}
            {t('panel.saveAndAssign')}
          </button>
        </div>
      </div>
    </div>
  );
}
